import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { sendContactEmail } from '../../../services/emailService';
import { Checkbox } from '../../../components/ui/Checkbox';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    inquiryType: '',
    message: '',
    urgency: '',
    preferredContact: '',
    newsletter: false,
    terms: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const inquiryTypes = [
    { value: 'quote', label: 'Request Quote' },
    { value: 'consultation', label: 'Technical Consultation' },
    { value: 'partnership', label: 'Business Partnership' },
    { value: 'visit', label: 'Factory Visit' },
    { value: 'support', label: 'Customer Support' },
    { value: 'other', label: 'Other Inquiry' }
  ];

  const urgencyLevels = [
    { value: 'low', label: 'Standard (3-5 days)' },
    { value: 'medium', label: 'Priority (1-2 days)' },
    { value: 'high', label: 'Urgent (Same day)' }
  ];

  const contactPreferences = [
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone Call' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'visit', label: 'In-Person Meeting' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s-()]{10,}$/?.test(formData?.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData?.inquiryType) {
      newErrors.inquiryType = 'Please select an inquiry type';
    }

    if (!formData?.message?.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData?.message?.trim()?.length < 10) {
      newErrors.message = 'Message must be at least 10 characters long';
    }

    if (!formData?.terms) {
      newErrors.terms = 'Please accept the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare inquiry details
      const inquiryTypeLabel = inquiryTypes?.find(type => type?.value === formData?.inquiryType)?.label || formData?.inquiryType;
      const urgencyLabel = urgencyLevels?.find(level => level?.value === formData?.urgency)?.label || 'Standard';
      const contactMethodLabel = contactPreferences?.find(pref => pref?.value === formData?.preferredContact)?.label || 'Any method';
      
      // Send email notification
      const emailResult = await sendContactEmail({
        name: formData?.name,
        email: formData?.email,
        phone: formData?.phone,
        company: formData?.company,
        subject: inquiryTypeLabel,
        inquiryType: inquiryTypeLabel,
        message: formData?.message,
        urgency: urgencyLabel,
        preferredContact: contactMethodLabel,
        newsletter: formData?.newsletter
      });
      
      // Format message for WhatsApp
      const whatsappMessage = `*New Inquiry from Website*%0A%0A` +
        `*Name:* ${formData?.name}%0A` +
        `*Email:* ${formData?.email}%0A` +
        `*Phone:* ${formData?.phone}%0A` +
        `*Company:* ${formData?.company || 'Not provided'}%0A` +
        `*Inquiry Type:* ${inquiryTypeLabel}%0A` +
        `*Urgency:* ${urgencyLabel}%0A` +
        `*Preferred Contact:* ${contactMethodLabel}%0A%0A` +
        `*Message:*%0A${formData?.message}%0A%0A` +
        `*Newsletter:* ${formData?.newsletter ? 'Yes' : 'No'}`;
      
      // Send to WhatsApp (opens WhatsApp with pre-filled message)
      const whatsappNumber = '919974056700'; // +91 99740 56700
      window.open(`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`, '_blank');
      
      // Show success message
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        inquiryType: '',
        message: '',
        urgency: '',
        preferredContact: '',
        newsletter: false,
        terms: false
      });
      
      // Show email status
      if (!emailResult?.success) {
        console.warn('Email notification may not have been sent. Please check your configuration.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('There was an error processing your request. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section className="py-8 sm:py-12 lg:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-success/10 mb-4 sm:mb-6">
              <Icon name="CheckCircle" size={36} className="text-success sm:w-12 sm:h-12" />
            </div>
            
            <h2 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-poppins font-bold text-text-primary mb-3 sm:mb-4">
              Thank You for Reaching Out!
            </h2>
            
            <p className="text-sm sm:text-base lg:text-lg text-text-secondary mb-6 sm:mb-8 max-w-2xl mx-auto">
              We've received your inquiry and our team will get back to you within 2 hours during business hours. You'll receive a confirmation email shortly.
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
              <div className="text-center p-3 sm:p-4 bg-surface rounded-lg">
                <Icon name="Clock" size={28} className="text-primary mx-auto mb-2 sm:w-8 sm:h-8" />
                <h3 className="text-sm sm:text-base font-semibold text-text-primary">Quick Response</h3>
                <p className="text-xs sm:text-sm text-text-secondary">Within 2 hours</p>
              </div>
              <div className="text-center p-3 sm:p-4 bg-surface rounded-lg">
                <Icon name="Mail" size={28} className="text-primary mx-auto mb-2 sm:w-8 sm:h-8" />
                <h3 className="text-sm sm:text-base font-semibold text-text-primary">Email Confirmation</h3>
                <p className="text-xs sm:text-sm text-text-secondary">Check your inbox</p>
              </div>
              <div className="text-center p-3 sm:p-4 bg-surface rounded-lg">
                <Icon name="Users" size={28} className="text-primary mx-auto mb-2 sm:w-8 sm:h-8" />
                <h3 className="text-sm sm:text-base font-semibold text-text-primary">Expert Team</h3>
                <p className="text-xs sm:text-sm text-text-secondary">Dedicated support</p>
              </div>
            </div>
            
            <Button
              variant="default"
              size="lg"
              className="text-sm sm:text-base font-semibold"
              onClick={() => setIsSubmitted(false)}
              iconName="ArrowLeft"
              iconPosition="left"
            >
              Send Another Message
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-8 sm:py-12 lg:py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-6 sm:mb-8 lg:mb-12">
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-poppins font-bold text-text-primary mb-2 sm:mb-3 lg:mb-4">
            Send Us a Message
          </h2>
          <p className="text-sm sm:text-base lg:text-lg text-text-secondary max-w-2xl mx-auto px-2">
            Fill out the form below and our textile experts will get back to you with personalized solutions.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5 lg:space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Full Name"
              type="text"
              placeholder="Enter your full name"
              value={formData?.name}
              onChange={(e) => handleInputChange('name', e?.target?.value)}
              error={errors?.name}
              required
            />

            <Input
              label="Email Address"
              type="email"
              placeholder="your.email@company.com"
              value={formData?.email}
              onChange={(e) => handleInputChange('email', e?.target?.value)}
              error={errors?.email}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Phone Number"
              type="tel"
              placeholder="+91 00000 00000"
              value={formData?.phone}
              onChange={(e) => handleInputChange('phone', e?.target?.value)}
              error={errors?.phone}
              required
            />

            <Input
              label="Company Name"
              type="text"
              placeholder="Your company name (optional)"
              value={formData?.company}
              onChange={(e) => handleInputChange('company', e?.target?.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Select
              label="Inquiry Type"
              placeholder="Select inquiry type"
              options={inquiryTypes}
              value={formData?.inquiryType}
              onChange={(value) => handleInputChange('inquiryType', value)}
              error={errors?.inquiryType}
              required
            />

            <Select
              label="Response Urgency"
              placeholder="Select urgency level"
              options={urgencyLevels}
              value={formData?.urgency}
              onChange={(value) => handleInputChange('urgency', value)}
            />
          </div>

          <Select
            label="Preferred Contact Method"
            placeholder="How would you like us to contact you?"
            options={contactPreferences}
            value={formData?.preferredContact}
            onChange={(value) => handleInputChange('preferredContact', value)}
          />

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Message <span className="text-error">*</span>
            </label>
            <textarea
              className="w-full h-32 resize-none rounded-md border border-gray-200 bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 transition-colors"
              placeholder="Tell us about your fabric requirements, quantity needed, or any questions you have..."
              value={formData?.message}
              onChange={(e) => handleInputChange('message', e?.target?.value)}
              required
            />
            {errors?.message && (
              <p className="mt-1 text-sm text-error">{errors?.message}</p>
            )}
          </div>

          <div className="space-y-4">
            <Checkbox
              label="Subscribe to our newsletter for textile industry insights and updates"
              checked={formData?.newsletter}
              onChange={(e) => handleInputChange('newsletter', e?.target?.checked)}
            />

            <Checkbox
              label="I agree to the terms and conditions and privacy policy"
              checked={formData?.terms}
              onChange={(e) => handleInputChange('terms', e?.target?.checked)}
              error={errors?.terms}
              required
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              type="submit"
              variant="default"
              size="lg"
              loading={isSubmitting}
              iconName="Send"
              iconPosition="right"
              className="btn-textile"
            >
              {isSubmitting ? 'Sending Message...' : 'Send Message'}
            </Button>

            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={() => {
                setFormData({
                  name: '',
                  email: '',
                  phone: '',
                  company: '',
                  inquiryType: '',
                  message: '',
                  urgency: '',
                  preferredContact: '',
                  newsletter: false,
                  terms: false
                });
                setErrors({});
              }}
              iconName="RotateCcw"
              iconPosition="left"
            >
              Reset Form
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default ContactForm;